/**
 * @file hooks/biz/list/main 核心区域hooks
 * @author zhangzhe(zhangzhe@baidu.com)
 */

import {useState, useMemo, useCallback, useEffect} from 'react';
import _ from 'lodash';
import {useInterval} from '@huse/timeout';

import useFetch from '../fetch';
import {getGlobalError, message} from '../../../utils/bizUtil';
import {ListOptions} from './interface';
import {useLocale} from '../../../components/locale';

const getErrorMsg = (error: any) => {
    return error ? (getGlobalError(error) || '获取记录失败') : '';
};

const DEFAULT_PAGESIZE = 10;
const DEFAULT_PAGESIZE_OPTIONS = ['10', '20', '50', '100'];

const useMain = (
    options: ListOptions,
    actions: any, // 业务端redux的actions
    handleTableCommand: (type: string, payload?: any) => void,
    handleAction: (config: any, payload: any) => void,
    customFunctions: any
) => {
    // 基本配置
    const {fetchListAction, rowKey, filterConfig, tableConfig, editComp,
        pageSize: defaultPageSize, pageSizeOptions, select = 'multi', interval, emptyText: optionsEmptyText} = options;
    // 勾子回调函数
    const {$onRequest, $onResponse, $onError, requestAbort = false} = options;
    // 自定义组件
    const {tableComp, card} = options;

    // 初始化不为空的筛选项
    const defaultFilterData = {};
    if (filterConfig && _.isArray(filterConfig.controls)) {
        _.each(filterConfig.controls, control => {
            if (control.name && !_.isNil(control.value)) {
                defaultFilterData[control.name] = control.value;
            }
        });
    }

    // List要用到的核心数据
    const [pageNo, setPageNo] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(defaultPageSize || DEFAULT_PAGESIZE);
    const [filterData, setFilterData] = useState<any>(defaultFilterData); // 筛选项
    const [order, setOrder] = useState<string>('desc'); // 'asc' || 'desc'
    const [orderBy, setOrderBy] = useState<string>('');
    const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]); // 勾选项
    const [requestCache, setRequestCache]: [any[], any] = useState<any[]>([]); // 历史查询记录
    const [listRequestAbort, setListRequestAbort] = useState<boolean>(requestAbort); // 是否禁止发起列表数据查询标志
    const [totalCount, setTotalCount] = useState<number>(0);
    const [dataSource, setDataSource] = useState<object[]>([]); // 表格中的数据
    const [locales] = useLocale('useList');

    const listParams = useMemo(
        () => {
            const params: any = {
                ...filterData,
                pageNo,
                pageSize
            };
            if (orderBy) {
                params.order = order;
                params.orderBy = orderBy;
            }
            // 清空所有为空的筛选项，这里可能会存在坑-zhangzhe FIXME
            _.each(params, (value, key) => {
                if (value === '' || value === null || (_.isArray(value) && value.length === 0)) {
                    delete params[key];
                }
            });
            return params;
        },
        [pageNo, pageSize, filterData, order, orderBy]
    );

    // 获取列表数据
    const $onListResponse = useCallback((page: any, requestParams: any) => {
        const responsePage = _.isFunction($onResponse)
            ? ($onResponse(page, listParams) || page) : page;
        setDataSource(responsePage.result);
        setTotalCount(responsePage.totalCount);
        // zhangzhe 2019-10-30 记录历史查询记录来解决“最新结果不是最后查询的问题”
        setRequestCache((prevRequestCache: any) => ([
            ...prevRequestCache,
            {
                request: requestParams,
                response: responsePage
            }
        ]));
        // zhangzhe 2019-04-16 特殊逻辑，当totalCount > 0 但是没有数据的时候，查找上一页, 主要场景：最后一页数据全部删除后的情况下兼容
        if (responsePage.totalCount > 0 && responsePage.pageNo > 1 && !responsePage.result.length) {
            setPageNo(pageNo - 1);
        }
        return responsePage;
    }, [$onResponse, listParams, pageNo]);
    const $onListError = useCallback((err: any) => {
        if (_.isFunction($onError)) {
            $onError(err);
        }
        const errorMsg = getErrorMsg(err);
        errorMsg && message('error', errorMsg);
    }, [$onError]);
    const {loading, error, reload, latestPayload}
        = useFetch(actions[fetchListAction], listParams, $onRequest, $onListResponse, $onListError, listRequestAbort);
    // 2019-09-06 轮询功能 TODO优化：当前轮询节奏不会变，即用户手动发起一次请求，并不会影响轮询的间隔
    useInterval(() => reload(false), (interval || -1));

    // 激活列表查询开关
    const activeListRequest = useCallback(() => listRequestAbort && setListRequestAbort(false), [listRequestAbort]);
    const clearRowKeysAndActive = useCallback(() => {
        activeListRequest();
        setSelectedRowKeys([]);
    }, [activeListRequest]);

    // 重新刷新列表
    const loadList = useCallback(() => {
        // 刷新前清空勾选
        setSelectedRowKeys([]);
        // zhangzhe 2019-11-07 重新发起查询的方法：这里看起来非常诡异，但能够保证重新发的请求回调内可以拿到最新数据，比如最新的props
        setListRequestAbort(true);
        setTimeout(() => setListRequestAbort(false), 0);
    }, []);
    // 跳到第一页重新刷新列表
    const loadFirstPage = useCallback(() => {
        clearRowKeysAndActive();
        if (pageNo !== 1) {
            setPageNo(1);
        }
        else {
            loadList();
        }
    }, [clearRowKeysAndActive, pageNo, loadList]);

    // 筛选项修改，只修改filterData，不发起查询！
    const handleFilterDataChange = useCallback((itemChanged: any) => {
        // 不允许发起查询
        setListRequestAbort(true);
        setFilterData({
            ...filterData,
            [itemChanged.name]: itemChanged.value
        });
    }, [filterData]);
    // 发起筛选
    const handleFilter = useCallback((newfilterData: any) => {
        // 根据filterData查询表格数据
        // zhangzhe 2019-05-21 如果是字符串，则做trim处理
        _.each(newfilterData, (value, key) => {
            if (_.isString(value)) {
                newfilterData[key] = _.trim(value);
            }
        });

        const mergeFilterData = {...filterData, ...newfilterData};
        if (pageNo === 1 && _.isEqual(filterData, mergeFilterData)) {
            // 如果筛选条件没变直接reloadList
            loadList();
        }
        else {
            // 如果有变化，会自动发起新一次查询
            setPageNo(1);
            setFilterData(mergeFilterData);
            clearRowKeysAndActive();
        }
    }, [pageNo, filterData, loadList, clearRowKeysAndActive]);

    // 表格中事件
    // 页数修改处理
    const handlePageSizeChange = useCallback((__: any, currentPageSize: number) => {
        clearRowKeysAndActive();
        setPageNo(1);
        setPageSize(currentPageSize);
    }, [clearRowKeysAndActive]);
    // 页码修改
    const handlePageChange = useCallback((currentPageNo: number) => {
        clearRowKeysAndActive();
        // 翻页后会直接发起查询
        setPageNo(currentPageNo);
    }, [clearRowKeysAndActive]);
    // 表头筛选和排序
    const handleTableChange = useCallback((__: any, filters?: any, sorter?: any) => {
        clearRowKeysAndActive();
        setPageNo(1);
        if (!_.isEmpty(sorter)) {
            const newOrderBy = sorter.order ? sorter.field : '';
            const newOrder = sorter.order ? (sorter.order === 'ascend' ? 'asc' : 'desc') : '';
            setOrderBy(newOrderBy);
            setOrder(newOrder);
        }
        if (!_.isEmpty(filters)) {
            setFilterData({
                ...filterData,
                ...filters
            });
        }
    }, [clearRowKeysAndActive, filterData]);
    // 处理选择变化
    const handleSelectChange = useCallback((newSelectedRowKeys: never[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
        customFunctions.handleSelectChange && customFunctions.handleSelectChange(newSelectedRowKeys);
    }, [customFunctions]);

    // 全选/全不选
    const checkAll = useCallback((checked: boolean) => {
        const newSelectedRowKeys: any[] = checked
            ? (rowKey ? _.map(dataSource, rowKey) : _.keys(dataSource))
            : [];
        setSelectedRowKeys(newSelectedRowKeys);
    }, [dataSource, rowKey]);

    useEffect(() => {
        // FIXME: 这个处理可能存在未知问题..
        if (requestCache.length) {
            const lastRequestCache = requestCache[requestCache.length - 1];
            if (!_.isEqual(lastRequestCache.request, latestPayload)) {
                // 走到这里说明顺序错了
                const lastMatchRequest = _.findLast(requestCache, cache => _.isEqual(cache.request, latestPayload));
                if (lastMatchRequest) {
                    setDataSource(lastMatchRequest.response.result);
                    setTotalCount(lastMatchRequest.response.totalCount);
                }
            }
        }
    }, [latestPayload, requestCache]);

    // zhangzhe 2020-02-24 如果fetchListAction修改，且用户配置的requestAbort不为true，则强制发起一次查询
    useEffect(() => {
        if (!requestAbort) {
            activeListRequest();
        }
    }, [fetchListAction]);

    // 下面是用来渲染的数据
    // 核心列表区域需要的数据准备
    const emptyText = loading ? locales?.loadingData : (getErrorMsg(error) || optionsEmptyText || locales?.noData);
    const locale = {
        emptyText
    };
    const tableProps: any = {
        rowKey: rowKey || ((__: any, index: number) => index),
        dataSource,
        locale,
        loading
    };
    // 判断是否需要多选框
    if (select !== 'none') {
        tableProps.rowSelection = {
            selectedRowKeys,
            onChange: handleSelectChange,
            type: select === 'single' ? 'radio' : 'checkbox'
        };
    }
    let tableType = '';
    if (tableComp) {
        tableType = 'custom';
        _.extend(tableProps, {
            handleAction,
            handleTableCommand
        });
    }
    else if (card) {
        tableType = 'card';
        _.extend(tableProps, {
            card,
            onHandleAction: handleAction,
            onHandleTableCommand: handleTableCommand
        });
    }
    else if (tableConfig) {
        tableType = 'table';
        _.extend(tableProps, tableConfig, {
            pagination: false,
            onChange: handleTableChange
        });
        if (editComp) {
            tableProps.components = {body: editComp};
        }
        // 选择框配置
        if (select !== 'none' && tableConfig.getCheckboxProps) {
            tableProps.rowSelection.getCheckboxProps = tableConfig.getCheckboxProps;
        }
    }
    // 分页栏数据
    const paginationProps = {
        total: totalCount,
        current: pageNo,
        pageSize,
        defaultPageSize: pageSize,
        pageSizeOptions: pageSizeOptions || DEFAULT_PAGESIZE_OPTIONS,
        showSizeChanger: true,
        showTotal: () => `${locales?.total}${totalCount}${locales?.stripData}`,
        onShowSizeChange: handlePageSizeChange,
        onChange: handlePageChange
    };
    // 用来渲染列表主区域的属性
    const mainListProps = {
        tableType,
        tableProps,
        tableComp,
        paginationProps
    };
    // 是否全选
    const checkedAll: boolean = dataSource.length === selectedRowKeys.length && selectedRowKeys.length > 0;
    return {
        // 数据
        mainListProps,
        tableData: {
            loading,
            error,
            checkedAll,
            pageNo,
            pageSize,
            dataSource,
            totalCount,
            order,
            orderBy,
            listParams,
            filterData,
            selectedRowKeys
        },
        // 暴露一些setState的方法供其他hooks使用
        setStates: {
            setPageNo,
            setPageSize,
            setTotalCount,
            setDataSource,
            setOrder,
            setOrderBy,
            setFilterData,
            setSelectedRowKeys
        },
        // api
        mainApi: {
            checkAll,
            loadList,
            loadFirstPage,
            handleFilterDataChange,
            handleFilter
        }
    };
};

export default useMain;
